/**
 * Close-on-outside-click / Escape, for the popovers the portal opens from a
 * trigger: the TopBar menus and the per-row kebab.
 *
 * A press rather than `click`, so a press that starts outside dismisses before
 * the click lands — otherwise the same gesture that closes the menu also
 * activates whatever was underneath it.
 *
 * `pointerdown` and not `mousedown` (#111): iOS Safari sends the compatibility
 * mouse events only for a tap on an element it considers clickable, so a tap on
 * plain page area left an open menu open on an iPhone. A pointer event is sent
 * for every tap, and for every mouse press as before.
 */

import { useEffect, type RefObject } from 'react'

export function useDismissOnOutsideClick(
  ref: RefObject<HTMLElement | null>,
  onDismiss: () => void,
): void {
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) onDismiss()
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onDismiss()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [ref, onDismiss])
}
